import { 
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from "@mui/material";
import { useState } from "react";
import ApiFetch from "../../services/api.fetch";
import { useUserAuth } from "../../context/UserAuthContext";
import { useTranslation } from "react-i18next";

interface CreateProfileModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (profileId: number) => void;
  candidateId?: string;
}

const CreateProfileModal = ({ open, onClose, onSuccess, candidateId }: CreateProfileModalProps) => {
  const { user } = useUserAuth();
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [screeningType, setScreeningType] = useState<'RENTER' | 'GUARANTOR'>('RENTER');
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      setError(t('screening_profile.create_modal.error_required'));
      return;
    }

    try {
      // Create the profile
      const response = await ApiFetch.httpPost('/v1/tenant/profile/createProfile', {
        firstname: firstName.trim(),
        surname: lastName.trim(),
        screeningType,
        employmentType: 'EMPLOYEE',
        tenantId: user?.tenant.id
      });

      // Add the profile to the current screening if candidateId is provided
      if (candidateId) {
        await ApiFetch.httpPost('/v1/tenant/profile/createCandidateProfile', {
          candidateId: parseInt(candidateId),
          profileId: response.id
        });
      }

      onSuccess(response.id);
      onClose();
    } catch (error) {
      setError(t('screening_profile.create_modal.error_failed'));
      console.error('Failed to create profile:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('screening_profile.create_modal.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <InputLabel>{t('screening_profile.create_modal.type')}</InputLabel>
            <Select
              value={screeningType}
              onChange={(e) => setScreeningType(e.target.value as 'RENTER' | 'GUARANTOR')}
              label={t('screening_profile.create_modal.type')}
            >
              <MenuItem value="RENTER">{t('screening_profile.renter')}</MenuItem>
              <MenuItem value="GUARANTOR">{t('screening_profile.guarantor')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label={t('screening_profile.create_modal.first_name')}
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            fullWidth
            error={!!error}
          />
          <TextField
            label={t('screening_profile.create_modal.last_name')}
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            fullWidth
            error={!!error}
            helperText={error}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('screening_profile.create_modal.cancel')}</Button>
        <Button 
          variant="contained" 
          onClick={handleSubmit}
          disabled={!firstName.trim() || !lastName.trim()}
        >
          {t('screening_profile.create_modal.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateProfileModal; 